import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AdminContentHeader } from "@/components/admin/AdminContentHeader";
import { PackageFilterBar } from "@/components/admin/PackageFilterBar";
import { PackagesTable, PackageTableItem } from "@/components/admin/PackagesTable";
import { BulkActionBar } from "@/components/admin/BulkActionBar";
import { PackageDetailDrawer } from "@/components/admin/PackageDetailDrawer";
import { AssignManagerDialog } from "@/components/admin/AssignManagerDialog";
import { EditPackageDialog } from "@/components/admin/EditPackageDialog";
import { ProposePickupDialog } from "@/components/admin/ProposePickupDialog";
import { CounterProposalManager } from "@/components/admin/CounterProposalManager";
// UserPickupRequestsManager removed - approval is now inline at user row level
import { DeliveryConfirmationDialog } from "@/components/admin/DeliveryConfirmationDialog";
import { PickupProofDialog } from "@/components/admin/PickupProofDialog";
import { ReschedulePackageDialog } from "@/components/admin/ReschedulePackageDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { CalendarPlus, Package, Archive } from "lucide-react";
import { notifyPackageDenied, notifyPickupDenied } from "@/lib/notifications";
import { HISTORY_PACKAGE_STATUSES } from "@/lib/pickupStatus";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface LogisticsManager {
  id: string;
  name: string;
  email: string;
  role: "admin" | "manager";
}

interface RawPackageData {
  id: string;
  manager_id: string | null;
  user_id: string;
  retailer: string;
  size: string;
  return_by_date: string | null;
  approval_status: string;
  pickup_schedules: Array<{
    id: string;
    pickup_date: string;
    pickup_time_slot: string;
  }>;
}

interface UserGroupData {
  userId: string;
  userName: string;
  email: string;
  address: string;
  subscriptionType: string | null;
  packages: {
    id: string;
    retailer: string;
    size: string;
    return_by_date: string | null;
    approval_status: string;
  }[];
}

export const AdminPackages = () => {
  const [packages, setPackages] = useState<PackageTableItem[]>([]);
  const [rawPackages, setRawPackages] = useState<RawPackageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logisticsManagers, setLogisticsManagers] = useState<LogisticsManager[]>([]);
  
  // Assignment is now inline at the user level via profiles.manager_id
  
  // Edit package dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<{
    id: string;
    managerId?: string;
    pickupDate?: Date;
    pickupTimeSlot?: string;
  } | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  
  // Approval dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvingPackageId, setApprovingPackageId] = useState<string | null>(null);
  const [approvalAction, setApprovalAction] = useState<"approve" | "deny">("approve");
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [denialReason, setDenialReason] = useState("");
  
  // Filters - read initial status from URL query params
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewTab, setViewTab] = useState<"active" | "history">(() => {
    return searchParams.get("view") === "history" ? "history" : "active";
  });
  const [selectedAssignee, setSelectedAssignee] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const statusParam = searchParams.get("status");
    if (statusParam === "pending_review") return "pending-review";
    if (statusParam === "archived") return "archived";
    return statusParam || "all";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("pickup-date-asc");
  
  // Propose pickup dialog state
  const [proposeDialogOpen, setProposeDialogOpen] = useState(false);
  const [proposeUserData, setProposeUserData] = useState<UserGroupData | null>(null);
  const [proposeLoading, setProposeLoading] = useState(false);
  const [userGroups, setUserGroups] = useState<Map<string, UserGroupData>>(new Map());
  
  // Delivery confirmation dialog state
  const [deliveryConfirmDialogOpen, setDeliveryConfirmDialogOpen] = useState(false);
  const [confirmingPackageId, setConfirmingPackageId] = useState<string | null>(null);
  
  // Pickup proof dialog state
  const [pickupProofDialogOpen, setPickupProofDialogOpen] = useState(false);
  const [pickupPackageId, setPickupPackageId] = useState<string | null>(null);
  
  // Reschedule dialog state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [reschedulePackageId, setReschedulePackageId] = useState<string | null>(null);

  const fetchLogisticsManagers = async () => {
    try {
      // Fetch users with admin or manager roles
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("role", ["admin", "manager"]);

      if (roleError) throw roleError;

      if (!roleData || roleData.length === 0) {
        setLogisticsManagers([]);
        return;
      }

      // Get unique user IDs with their roles
      const userRoleMap = new Map<string, "admin" | "manager">();
      roleData.forEach((r) => {
        // Prioritize admin role if user has both
        if (!userRoleMap.has(r.user_id) || r.role === "admin") {
          userRoleMap.set(r.user_id, r.role as "admin" | "manager");
        }
      });

      const userIds = Array.from(userRoleMap.keys());

      // Fetch profile data for these users
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name")
        .in("id", userIds);

      if (profileError) throw profileError;

      const managers: LogisticsManager[] = (profileData || []).map((profile: any) => {
        const firstName = profile.first_name || "";
        const lastName = profile.last_name || "";
        const fullName = firstName && lastName
          ? `${firstName} ${lastName}`
          : profile.email?.split("@")[0] || "Unknown";

        return {
          id: profile.id,
          name: fullName,
          email: profile.email || "",
          role: userRoleMap.get(profile.id) || "manager",
        };
      });

      setLogisticsManagers(managers);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching logistics managers:", error);
      }
    }
  };

  const fetchPackages = async () => {
    try {
      let query = (supabase as any)
        .from("packages")
        .select(`
          *,
          pickup_schedules (*),
          profiles!packages_user_id_fkey (id, email, phone_number, street, city, state, zip, first_name, last_name, subscription_type, manager_id)
        `)
        .order("created_at", { ascending: false });

      const historyStatuses = [...HISTORY_PACKAGE_STATUSES] as string[];
      
      if (viewTab === "history") {
        query = query.in("status", historyStatuses);
      } else {
        for (const s of historyStatuses) {
          query = query.not("status", "eq", s);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      // Also fetch manager profiles separately for assigned packages
      const managerIds: string[] = Array.from(new Set((data || []).filter((p: any) => p.manager_id).map((p: any) => String(p.manager_id))));
      let managerMap: Record<string, { name: string }> = {};
      
      if (managerIds.length > 0) {
        const { data: managerData } = await supabase
          .from("profiles")
          .select("id, email, first_name, last_name")
          .in("id", managerIds as string[]);
        
        (managerData || []).forEach((m: any) => {
          const firstName = m.first_name || "";
          const lastName = m.last_name || "";
          const name = firstName && lastName ? `${firstName} ${lastName}` : m.email?.split("@")[0] || "Unknown";
          managerMap[m.id] = { name };
        });
      }

      // Store raw data for editing
      const rawData: RawPackageData[] = (data || []).map((pkg: any) => ({
        id: pkg.id,
        manager_id: pkg.manager_id,
        user_id: pkg.user_id,
        retailer: pkg.retailer,
        size: pkg.size,
        return_by_date: pkg.return_by_date,
        approval_status: pkg.approval_status,
        pickup_schedules: pkg.pickup_schedules || [],
      }));
      setRawPackages(rawData);

      // Build user groups for user-level pickup scheduling
      const groups = new Map<string, UserGroupData>();
      (data || []).forEach((pkg: any) => {
        const userId = pkg.user_id;
        const profile = pkg.profiles;
        
        if (!groups.has(userId)) {
          const firstName = profile?.first_name || "";
          const lastName = profile?.last_name || "";
          const fullName = firstName && lastName 
            ? `${firstName} ${lastName}`
            : profile?.email?.split("@")[0] || "Unknown";
          
          const address = profile?.street && profile?.city && profile?.state && profile?.zip
            ? `${profile.street}, ${profile.city}, ${profile.state} ${profile.zip}`
            : "Address not available";
            
          groups.set(userId, {
            userId,
            userName: fullName,
            email: profile?.email || "Unknown",
            address,
            subscriptionType: profile?.subscription_type || null,
            packages: [],
          });
        }
        
        const group = groups.get(userId)!;
        group.packages.push({
          id: pkg.id,
          retailer: pkg.retailer,
          size: pkg.size,
          return_by_date: pkg.return_by_date,
          approval_status: pkg.approval_status,
        });
      });
      setUserGroups(groups);

      // Build owner map from profile manager_ids
      const profileManagerIds: string[] = Array.from(new Set(
        (data || [])
          .filter((p: any) => p.profiles?.manager_id)
          .map((p: any) => String(p.profiles.manager_id))
      ));
      let ownerMap: Record<string, { id: string; name: string }> = {};
      if (profileManagerIds.length > 0) {
        const { data: ownerData } = await supabase
          .from("profiles")
          .select("id, email, first_name, last_name")
          .in("id", profileManagerIds);
        (ownerData || []).forEach((o: any) => {
          const fn = o.first_name || "";
          const ln = o.last_name || "";
          const name = fn && ln ? `${fn} ${ln}` : o.email?.split("@")[0] || "Unknown";
          ownerMap[o.id] = { id: o.id, name };
        });
      }

      // Fetch user-level pickup data from user_pickups
      const userIds = Array.from(new Set((data || []).map((p: any) => p.user_id)));
      let userPickupMap: Record<string, { id: string; date: string; time: string; status: string; pickup_confirmation_photo_url: string | null }> = {};
      if (userIds.length > 0) {
        const { data: pickupData } = await (supabase as any)
          .from("user_pickups")
          .select("id, user_id, proposed_date, proposed_time_slot, status, pickup_confirmation_photo_url")
          .in("user_id", userIds)
          .in("status", ["awaiting_confirmation", "awaiting_admin_confirmation", "confirmed", "counter_proposed", "needs_user_response", "completed"])
          .order("created_at", { ascending: false });

        // Take the most recent active pickup per user
        (pickupData || []).forEach((p: any) => {
          if (!userPickupMap[p.user_id]) {
            userPickupMap[p.user_id] = {
              id: p.id,
              date: p.proposed_date,
              time: p.proposed_time_slot,
              status: p.status,
              pickup_confirmation_photo_url: p.pickup_confirmation_photo_url || null,
            };
          }
        });
      }

      const formattedPackages: PackageTableItem[] = (data || []).map((pkg: any) => {
        const firstName = pkg.profiles?.first_name || "";
        const lastName = pkg.profiles?.last_name || "";
        const fullName = firstName && lastName 
          ? `${firstName} ${lastName}`
          : pkg.profiles?.email?.split("@")[0] || "Unknown";
        
        const assignedOwner = pkg.profiles?.manager_id && ownerMap[pkg.profiles.manager_id]
          ? ownerMap[pkg.profiles.manager_id]
          : undefined;

        const userAddress = pkg.profiles?.street && pkg.profiles?.city && pkg.profiles?.state && pkg.profiles?.zip
          ? `${pkg.profiles.street}, ${pkg.profiles.city}, ${pkg.profiles.state} ${pkg.profiles.zip}`
          : undefined;

        const userPickup = userPickupMap[pkg.user_id];
        const pickupStatus: "not-scheduled" | "pending" | "approved" = userPickup
          ? ((userPickup.status === "confirmed" || userPickup.status === "completed") ? "approved" : "pending")
          : "not-scheduled";

        return {
          id: pkg.id,
          retailer: pkg.retailer,
          size: pkg.size,
          thumbnailUrl: pkg.package_photo_url,
          qrCodeUrl: pkg.qr_code_url,
          user: {
            id: pkg.user_id,
            fullName: fullName,
            email: pkg.profiles?.email || "Unknown",
            phone: pkg.profiles?.phone_number || undefined,
            address: userAddress,
            pickupDate: userPickup ? format(new Date(userPickup.date + "T00:00:00"), "MMM d, yyyy") : undefined,
            pickupTime: userPickup?.time || undefined,
            pickupStatus,
            pickupId: userPickup?.id || undefined,
            assignedOwner,
          },
          manager: pkg.manager_id && managerMap[pkg.manager_id]
            ? { id: pkg.manager_id, name: managerMap[pkg.manager_id].name }
            : undefined,
          proposedPickup: pkg.pickup_schedules?.[0]
            ? {
                date: format(new Date(pkg.pickup_schedules[0].pickup_date), "MMM d, yyyy"),
                timeWindow: pkg.pickup_schedules[0].pickup_time_slot,
                rawDate: pkg.pickup_schedules[0].pickup_date,
              }
            : undefined,
          approvalStatus: pkg.approval_status as "pending" | "approved" | "rejected",
          status: mapStatus(pkg.approval_status, pkg.status),
          packageStatus: pkg.status,
          canceledAt: pkg.canceled_at,
          cancelReason: pkg.cancel_reason,
          createdAt: pkg.created_at,
          confirmationCode: pkg.confirmation_code,
          proofOfDeliveryUrl: pkg.proof_of_delivery_url,
          proofOfPickupUrl: pkg.proof_of_pickup_url,
          returnReceiptUrl: pkg.return_receipt_url,
          pickupConfirmationPhotoUrl: userPickup?.pickup_confirmation_photo_url || null,
          returnedAt: pkg.returned_at,
          notes: pkg.notes,
        };
      });

      setPackages(formattedPackages);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching packages:", error);
      }
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const mapStatus = (approvalStatus: string, status: string): PackageTableItem["status"] => {
    if (approvalStatus === "pending") return "pending-review";
    if (approvalStatus === "rejected") return "archived";
    if (status === "archived") return "archived";
    if (status === "user_canceled") return "user-canceled";
    if (status === "returned") return "returned";
    if (status === "delivered") return "delivered";
    if (status === "picked_up") return "picked-up";
    if (status === "scheduled") return "pickup-scheduled";
    return "awaiting-confirmation";
  };

  useEffect(() => {
    fetchPackages();
    fetchLogisticsManagers();
  }, [viewTab]);

  // Reset status filter when switching tabs
  const handleTabChange = (tab: string) => {
    setViewTab(tab as "active" | "history");
    setSelectedStatus("all");
    setSelectedIds([]);
  };

  const handleRowClick = (pkg: PackageTableItem) => {
    const fullPkg = packages.find((p) => p.id === pkg.id) as any;
    const submittedDate = fullPkg?.createdAt 
      ? format(new Date(fullPkg.createdAt), "MMM d, yyyy 'at' h:mm a")
      : "Unknown";
    
    setSelectedPackage({
      ...fullPkg,
      submittedAt: submittedDate,
      timeline: [
        { id: "1", status: "Package Submitted", timestamp: submittedDate, description: "" },
      ],
    });
    setDrawerOpen(true);
  };

  const handleAssignUserManager = async (userId: string, managerId: string | null) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ manager_id: managerId })
        .eq("id", userId);

      if (error) throw error;

      const manager = logisticsManagers.find((m) => m.id === managerId);
      toast.success(managerId ? `User assigned to ${manager?.name || "manager"}` : "User unassigned");
      await fetchPackages();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error assigning user manager:", error);
      }
      toast.error("Failed to assign manager");
    }
  };

  // User-level pickup approval handlers
  const handleApprovePickup = async (userId: string) => {
    try {
      // Find the user's active pickup
      const pkg = packages.find(p => p.user.id === userId && p.user.pickupId);
      const pickupId = pkg?.user.pickupId;
      if (!pickupId) {
        toast.error("No active pickup found for this user");
        return;
      }

      const { error } = await (supabase as any)
        .from("user_pickups")
        .update({ status: "confirmed" })
        .eq("id", pickupId);

      if (error) throw error;

      toast.success("Pickup approved successfully");
      await fetchPackages();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error approving pickup:", error);
      }
      toast.error("Failed to approve pickup");
    }
  };

  // Pickup denial dialog state
  const [pickupDenyDialogOpen, setPickupDenyDialogOpen] = useState(false);
  const [pickupDenyUserId, setPickupDenyUserId] = useState<string | null>(null);
  const [pickupDenialReason, setPickupDenialReason] = useState("");
  const [pickupDenyLoading, setPickupDenyLoading] = useState(false);

  const handleDenyPickup = (userId: string) => {
    setPickupDenyUserId(userId);
    setPickupDenialReason("");
    setPickupDenyDialogOpen(true);
  };

  const confirmDenyPickup = async () => {
    if (!pickupDenyUserId || !pickupDenialReason.trim()) {
      toast.error("Please provide a reason for denying the pickup");
      return;
    }

    const pkg = packages.find(p => p.user.id === pickupDenyUserId && p.user.pickupId);
    const pickupId = pkg?.user.pickupId;
    if (!pickupId) {
      toast.error("No active pickup found for this user");
      return;
    }

    setPickupDenyLoading(true);
    try {
      const { error } = await (supabase as any)
        .from("user_pickups")
        .update({ 
          status: "needs_user_response",
          denial_reason: pickupDenialReason.trim(),
        })
        .eq("id", pickupId);

      if (error) throw error;

      // Notify the user
      await notifyPickupDenied(pickupDenyUserId, pickupDenialReason.trim());

      toast.success("Pickup denied — user has been notified to pick a new time");
      setPickupDenyDialogOpen(false);
      setPickupDenyUserId(null);
      setPickupDenialReason("");
      await fetchPackages();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error denying pickup:", error);
      }
      toast.error("Failed to deny pickup");
    } finally {
      setPickupDenyLoading(false);
    }
  };

  const handleNotifyAgain = async (packageId: string) => {
    toast.success("Notification sent to user");
  };

  const handleEditPackage = (packageId: string) => {
    const rawPkg = rawPackages.find((p) => p.id === packageId);
    if (!rawPkg) return;

    const pickupSchedule = rawPkg.pickup_schedules?.[0];
    setEditingPackage({
      id: packageId,
      managerId: rawPkg.manager_id || undefined,
      pickupDate: pickupSchedule ? new Date(pickupSchedule.pickup_date) : undefined,
      pickupTimeSlot: pickupSchedule?.pickup_time_slot || undefined,
    });
    setEditDialogOpen(true);
  };

  const handleSavePackage = async (data: {
    packageId: string;
    managerId: string | null;
    pickupDate: Date | null;
    pickupTimeSlot: string | null;
  }) => {
    setEditLoading(true);
    try {
      // Update package manager
      const { error: packageError } = await (supabase as any)
        .from("packages")
        .update({ 
          manager_id: data.managerId,
          status: data.pickupDate && data.pickupTimeSlot ? "scheduled" : "pending"
        })
        .eq("id", data.packageId);

      if (packageError) throw packageError;

      // Handle pickup schedule
      const rawPkg = rawPackages.find((p) => p.id === data.packageId);
      const existingSchedule = rawPkg?.pickup_schedules?.[0];

      if (data.pickupDate && data.pickupTimeSlot) {
        // Get user's address for the pickup
        const pkg = packages.find((p) => p.id === data.packageId);
        const { data: profileData } = await supabase
          .from("profiles")
          .select("street, city, state, zip")
          .eq("id", pkg?.user.id)
          .single();

        const address = profileData
          ? `${profileData.street || ""}, ${profileData.city || ""}, ${profileData.state || ""} ${profileData.zip || ""}`.trim()
          : "Address not available";

        if (existingSchedule) {
          // Update existing schedule
          const { error: scheduleError } = await (supabase as any)
            .from("pickup_schedules")
            .update({
              pickup_date: format(data.pickupDate, "yyyy-MM-dd"),
              pickup_time_slot: data.pickupTimeSlot,
            })
            .eq("id", existingSchedule.id);

          if (scheduleError) throw scheduleError;
        } else {
          // Create new schedule
          const { error: scheduleError } = await (supabase as any)
            .from("pickup_schedules")
            .insert({
              package_id: data.packageId,
              user_id: rawPkg?.user_id,
              pickup_date: format(data.pickupDate, "yyyy-MM-dd"),
              pickup_time_slot: data.pickupTimeSlot,
              address: address,
            });

          if (scheduleError) throw scheduleError;
        }
      } else if (existingSchedule && !data.pickupDate) {
        // Remove schedule if date is cleared
        await (supabase as any)
          .from("pickup_schedules")
          .delete()
          .eq("id", existingSchedule.id);
      }

      toast.success("Package updated successfully");
      await fetchPackages();
      setEditDialogOpen(false);
      setEditingPackage(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error updating package:", error);
      }
      toast.error("Failed to update package");
    } finally {
      setEditLoading(false);
    }
  };

  const handleBulkAssignManager = async (managerId: string) => {
    try {
      const { error } = await (supabase as any)
        .from("packages")
        .update({ manager_id: managerId })
        .in("id", selectedIds);

      if (error) throw error;

      toast.success(`Assigned ${selectedIds.length} packages to manager`);
      await fetchPackages();
      setSelectedIds([]);
    } catch (error) {
      toast.error("Failed to assign packages");
    }
  };

  const handleBulkSchedule = (date: Date, timeSlot: string) => {
    toast.success(`Scheduled ${selectedIds.length} packages for ${format(date, "MMM d")} at ${timeSlot}`);
    setSelectedIds([]);
  };

  const handleArchivePackage = async (packageId: string) => {
    try {
      const { error } = await (supabase as any)
        .from("packages")
        .update({ 
          archived_at: new Date().toISOString(),
          status: "archived"
        })
        .eq("id", packageId);

      if (error) throw error;

      toast.success("Package archived successfully");
      await fetchPackages();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error archiving package:", error);
      }
      toast.error("Failed to archive package");
    }
  };

  // User-level pickup proposal handler
  const handleProposePickupForUser = (userId: string) => {
    const userData = userGroups.get(userId);
    if (!userData) return;
    
    setProposeUserData(userData);
    setProposeDialogOpen(true);
  };

  const handleProposePickupConfirm = async (data: {
    userId: string;
    proposedDate: Date;
    proposedTimeSlot: string;
    managerId: string | null;
    address: string;
    packageIds: string[];
  }) => {
    setProposeLoading(true);
    try {
      // Create user-level pickup
      const { data: pickupData, error: pickupError } = await (supabase as any)
        .from("user_pickups")
        .insert({
          user_id: data.userId,
          proposed_date: format(data.proposedDate, "yyyy-MM-dd"),
          proposed_time_slot: data.proposedTimeSlot,
          status: "awaiting_confirmation",
          address: data.address,
          manager_id: data.managerId,
        })
        .select()
        .single();

      if (pickupError) throw pickupError;

      // Link approved packages to this pickup
      if (data.packageIds.length > 0) {
        const { error: linkError } = await (supabase as any)
          .from("packages")
          .update({ 
            pickup_id: pickupData.id,
            status: "scheduled"
          })
          .in("id", data.packageIds);

        if (linkError) throw linkError;
      }

      toast.success("Pickup proposed successfully. Awaiting user confirmation.");
      await fetchPackages();
      setProposeDialogOpen(false);
      setProposeUserData(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error proposing pickup:", error);
      }
      toast.error("Failed to propose pickup");
    } finally {
      setProposeLoading(false);
    }
  };

  // Get users with approved packages for quick action
  const usersWithApprovedPackages = useMemo(() => {
    const users: { userId: string; userName: string; approvedCount: number }[] = [];
    userGroups.forEach((group) => {
      const approvedCount = group.packages.filter(p => p.approval_status === "approved").length;
      if (approvedCount > 0) {
        users.push({
          userId: group.userId,
          userName: group.userName,
          approvedCount,
        });
      }
    });
    return users;
  }, [userGroups]);

  // Approval handlers
  const handleApprovalAction = (packageId: string, action: "approve" | "deny") => {
    setApprovingPackageId(packageId);
    setApprovalAction(action);
    setDenialReason("");
    setApprovalDialogOpen(true);
  };

  const confirmApproval = async () => {
    if (!approvingPackageId) return;

    // Require denial reason when denying
    if (approvalAction === "deny" && !denialReason.trim()) {
      toast.error("Please provide a reason for denying the package");
      return;
    }

    setApprovalLoading(true);
    try {
      const updateData: { approval_status: string; rejection_reason?: string | null } = {
        approval_status: approvalAction === "approve" ? "approved" : "rejected",
      };

      if (approvalAction === "deny") {
        updateData.rejection_reason = denialReason.trim();
      } else {
        updateData.rejection_reason = null;
      }

      const { error } = await (supabase as any)
        .from("packages")
        .update(updateData)
        .eq("id", approvingPackageId);

      if (error) throw error;

      // Send notification if package was denied
      if (approvalAction === "deny") {
        const pkg = packages.find(p => p.id === approvingPackageId);
        const rawPkg = rawPackages.find(p => p.id === approvingPackageId);
        if (pkg?.user.id && rawPkg) {
          await notifyPackageDenied(
            pkg.user.id,
            rawPkg.retailer,
            denialReason.trim()
          );
        }
      }

      toast.success(
        approvalAction === "approve"
          ? "Package approved successfully"
          : "Package denied successfully"
      );
      await fetchPackages();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error updating package approval:", error);
      }
      toast.error("Failed to update package status");
    } finally {
      setApprovalLoading(false);
      setApprovalDialogOpen(false);
      setApprovingPackageId(null);
      setDenialReason("");
    }
  };

  // Filter packages by assignee (user-level), status, and search
  const filteredPackages = useMemo(() => {
    return packages.filter((pkg) => {
      // Assignee filter - operates at user level (profiles.manager_id)
      const matchesAssignee = 
        selectedAssignee === "all" || 
        (selectedAssignee === "unassigned" && !pkg.user.assignedOwner) ||
        (selectedAssignee !== "unassigned" && pkg.user.assignedOwner?.id === selectedAssignee);
      
      const matchesStatus = selectedStatus === "all" || pkg.status === selectedStatus;
      const matchesSearch =
        searchQuery === "" ||
        pkg.user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pkg.user.email.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesAssignee && matchesStatus && matchesSearch;
    });
  }, [packages, selectedAssignee, selectedStatus, searchQuery]);

  // Sort filtered packages at user-group level
  const sortedPackages = useMemo(() => {
    // Group by user first to sort at user level
    const userGroups = new Map<string, typeof filteredPackages>();
    filteredPackages.forEach((pkg) => {
      const existing = userGroups.get(pkg.user.id) || [];
      existing.push(pkg);
      userGroups.set(pkg.user.id, existing);
    });

    // Sort groups
    const sortedGroups = Array.from(userGroups.entries()).sort(([, aPkgs], [, bPkgs]) => {
      const a = aPkgs[0];
      const b = bPkgs[0];

      switch (sortBy) {
        case "pickup-date-asc": {
          const aDate = a.user.pickupDate || "9999";
          const bDate = b.user.pickupDate || "9999";
          return aDate.localeCompare(bDate);
        }
        case "pickup-date-desc": {
          const aDate = a.user.pickupDate || "0000";
          const bDate = b.user.pickupDate || "0000";
          return bDate.localeCompare(aDate);
        }
        case "approval-approved-first": {
          const aApproved = a.user.pickupStatus === "approved" ? 0 : 1;
          const bApproved = b.user.pickupStatus === "approved" ? 0 : 1;
          return aApproved - bApproved;
        }
        case "approval-not-approved-first": {
          const aApproved = a.user.pickupStatus === "approved" ? 1 : 0;
          const bApproved = b.user.pickupStatus === "approved" ? 1 : 0;
          return aApproved - bApproved;
        }
        default:
          return 0;
      }
    });

    // Flatten back
    return sortedGroups.flatMap(([, pkgs]) => pkgs);
  }, [filteredPackages, sortBy]);

  // Count visible users
  const visibleUserCount = useMemo(() => {
    const userIds = new Set(sortedPackages.map((p) => p.user.id));
    return userIds.size;
  }, [sortedPackages]);

  // Count pending packages based on current filter
  const pendingCount = useMemo(() => {
    return sortedPackages.filter((p) => 
      p.status === "pending-review" || p.status === "awaiting-confirmation"
    ).length;
  }, [sortedPackages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading packages...</p>
      </div>
    );
  }

  // Get selected assignee name for display
  const selectedAssigneeName = selectedAssignee !== "all" && selectedAssignee !== "unassigned"
    ? logisticsManagers.find(m => m.id === selectedAssignee)?.name 
    : selectedAssignee === "unassigned" ? "Unassigned" : null;

  return (
    <div className="space-y-6">
      <AdminContentHeader
        title="All Packages"
        subtitle={
          selectedAssigneeName 
            ? `${visibleUserCount} users · ${sortedPackages.length} packages (${selectedAssigneeName})`
            : `${visibleUserCount} users · ${packages.length} total packages`
        }
        banner={
          viewTab === "active" && pendingCount > 0
            ? {
                message: `${pendingCount} packages need your attention`,
                variant: "warning",
              }
            : undefined
        }
      />

      <Tabs value={viewTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Active
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Archive className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* User Pickup Requests moved to inline row-level actions in PackagesTable */}

      {/* Counter-Proposals Section - Shows pickups needing rescheduling */}
      {viewTab === "active" && <CounterProposalManager onPickupUpdated={fetchPackages} />}

      <PackageFilterBar
        logisticsManagers={logisticsManagers}
        selectedAssignee={selectedAssignee}
        onAssigneeChange={setSelectedAssignee}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortBy={sortBy}
        onSortChange={setSortBy}
        visibleUserCount={visibleUserCount}
      />

      <PackagesTable
        packages={sortedPackages}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onRowClick={handleRowClick}
        onAssignUserManager={handleAssignUserManager}
        managers={logisticsManagers.map((m) => ({ id: m.id, name: m.name }))}
        onNotifyAgain={handleNotifyAgain}
        onEditPackage={handleEditPackage}
        
        onArchivePackage={handleArchivePackage}
        showStatusActions
        onApprovePackage={(id) => handleApprovalAction(id, "approve")}
        onDenyPackage={(id) => handleApprovalAction(id, "deny")}
        onApprovePickup={handleApprovePickup}
        onDenyPickup={handleDenyPickup}
        onMarkPickedUp={(id) => {
          setPickupPackageId(id);
          setPickupProofDialogOpen(true);
        }}
        onUploadConfirmation={(id) => {
          setConfirmingPackageId(id);
          setDeliveryConfirmDialogOpen(true);
        }}
        onReschedule={(id) => {
          setReschedulePackageId(id);
          setRescheduleDialogOpen(true);
        }}
        emptyMessage={
          selectedAssigneeName 
            ? `No packages for ${selectedAssigneeName}.`
            : "No packages found"
        }
      />

      <BulkActionBar
        selectedCount={selectedIds.length}
        managers={logisticsManagers.map((m) => ({ id: m.id, name: m.name }))}
        onAssignManager={handleBulkAssignManager}
        onSchedulePickup={handleBulkSchedule}
        onClearSelection={() => setSelectedIds([])}
      />

      <PackageDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        packageData={selectedPackage}
        actions={
          <Button className="w-full" onClick={() => setDrawerOpen(false)}>
            Close
          </Button>
        }
      />

      {/* AssignManagerDialog removed — assignment is now inline at user level */}

      <EditPackageDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        packageData={editingPackage}
        managers={logisticsManagers}
        onSave={handleSavePackage}
        loading={editLoading}
      />

      {/* Approval Confirmation Dialog */}
      <AlertDialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {approvalAction === "approve" ? "Approve Package" : "Deny Package"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {approvalAction === "approve"
                ? "This will approve the package for pickup scheduling. The user will be notified."
                : "Please provide a reason for denying this package. The user will be notified."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {approvalAction === "deny" && (
            <div className="py-2">
              <textarea
                value={denialReason}
                onChange={(e) => setDenialReason(e.target.value)}
                placeholder="Enter reason for denial (required)..."
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {denialReason.length}/500 characters
              </p>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmApproval}
              disabled={approvalLoading || (approvalAction === "deny" && !denialReason.trim())}
              className={approvalAction === "deny" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {approvalLoading ? "Processing..." : approvalAction === "approve" ? "Approve" : "Deny"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User-Level Pickup Proposal Dialog */}
      <ProposePickupDialog
        open={proposeDialogOpen}
        onOpenChange={setProposeDialogOpen}
        userData={proposeUserData}
        managers={logisticsManagers.map((m) => ({ id: m.id, name: m.name }))}
        onPropose={handleProposePickupConfirm}
        loading={proposeLoading}
      />

      {/* Delivery Confirmation Dialog */}
      <DeliveryConfirmationDialog
        open={deliveryConfirmDialogOpen}
        onOpenChange={setDeliveryConfirmDialogOpen}
        packageId={confirmingPackageId}
        onSuccess={() => {
          fetchPackages();
          setConfirmingPackageId(null);
        }}
      />

      {/* Pickup Proof Dialog */}
      <PickupProofDialog
        open={pickupProofDialogOpen}
        onOpenChange={setPickupProofDialogOpen}
        packageId={pickupPackageId}
        onSuccess={() => {
          fetchPackages();
          setPickupPackageId(null);
        }}
      />

      {/* Reschedule Package Dialog */}
      <ReschedulePackageDialog
        open={rescheduleDialogOpen}
        onOpenChange={setRescheduleDialogOpen}
        packageId={reschedulePackageId}
        onSuccess={() => {
          fetchPackages();
          setReschedulePackageId(null);
        }}
      />

      {/* Pickup Denial Dialog */}
      <AlertDialog open={pickupDenyDialogOpen} onOpenChange={setPickupDenyDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deny Pickup Time</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for denying this pickup request. The user will be notified and asked to select a new date and time.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-2">
            <textarea
              value={pickupDenialReason}
              onChange={(e) => setPickupDenialReason(e.target.value)}
              placeholder="Enter reason for denial (required)..."
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {pickupDenialReason.length}/500 characters
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDenyPickup}
              disabled={pickupDenyLoading || !pickupDenialReason.trim()}
              className="bg-destructive hover:bg-destructive/90"
            >
              {pickupDenyLoading ? "Processing..." : "Deny Pickup"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
